import { formatPromotionTerms } from '../../utils/priceListPromotions';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiMaximize, FiMinimize, FiPause, FiPlay } from 'react-icons/fi';
import useBranchDisplayClock from './useBranchDisplayClock';
import useDisplayWakeLock from './useDisplayWakeLock';
import useDisplayVideoFallback from './useDisplayVideoFallback';
import DisplayProductImage, { prepareDisplayImage } from './DisplayProductImage';
import { displayItemKey, displayPrice, displaySellingUnit, displaySlideIndex, displaySlides, formatDisplayPrice } from './branchDisplayUtils';
import './branchDisplay.css';

const ProductTile = ({ item, hero, index, now, showPromotions }) => {
  const [brokenImage, setBrokenImage] = useState(false);
  useEffect(() => setBrokenImage(false), [item.imageUrl]);
  const price = displayPrice(item, now, showPromotions);
  const title = item.productName || 'Producto destacado';
  const variety = item.varietyName && item.varietyName.toLowerCase() !== title.toLowerCase() ? item.varietyName : '';
  const hasImage = item.imageUrl && !brokenImage;
  return (
    <article
      className={`branch-display-tile ${hero ? 'branch-display-hero' : `branch-display-small branch-display-color-${index}`} ${hasImage ? '' : 'branch-display-no-image'}`}
      data-variety-price-id={item.varietyPriceId}
      data-product-id={item.productId}
      aria-label={`${title}${variety ? ` ${variety}` : ''}`}
    >
      <div className="branch-display-product-copy">
        {hero && <span className="branch-display-eyebrow">Destacado de hoy</span>}
        <h2 className={title.length > 35 ? 'branch-display-long-name' : title.length > 18 ? 'branch-display-medium-name' : ''}>{title}</h2>
        {variety && <p className="branch-display-variety">{variety}</p>}
        {item.formatLabel && <p className="branch-display-format">{item.formatLabel}</p>}
      </div>
      {hasImage && (
        <div className="branch-display-photo">
          <DisplayProductImage imageUrl={item.imageUrl} onError={() => setBrokenImage(true)} />
        </div>
      )}
      <div className="branch-display-price-area">
        <div className={`branch-display-price-tag ${String(Math.round(price.effectivePrice)).length > 6 ? 'branch-display-long-price' : ''}`}>
          {price.unitPrice && <span className="branch-display-offer-kind">{price.effectivePrice < Number(item.regularPrice) ? 'Oferta · Descuento' : 'Precio de oferta'}</span>}
          {price.unitPrice && price.effectivePrice < Number(item.regularPrice) && (
            <span className="branch-display-regular-price">Antes <s>{formatDisplayPrice(item.regularPrice)}</s></span>
          )}
          <strong><span className="branch-display-currency">$</span>{formatDisplayPrice(price.effectivePrice).replace(/^\$/, '')}</strong>
          <span className="branch-display-unit">{displaySellingUnit(item)}</span>
        </div>
        {price.packs.length > 0 && (
          <div className="branch-display-packs">
            <b className="branch-display-promotion-kind">Promoción por cantidad</b>
            {price.packs.map((pack) => <span key={pack.id}>{formatPromotionTerms(pack)}</span>)}
          </div>
        )}
      </div>
    </article>
  );
};

const SlideBoard = ({ items, now, showPromotions, previous = false }) => (
  <div className={`branch-display-board ${items.length === 1 ? 'branch-display-board-single' : ''} ${items.length > 4 ? `branch-display-board-grid branch-display-board-${items.length}` : ''} ${previous ? 'branch-display-board-previous' : 'branch-display-board-current'}`} aria-hidden={previous || undefined}>
    {items.length > 4 ? items.map((item, index) => (
      <ProductTile key={displayItemKey(item)} item={item} index={index % 3} now={now} showPromotions={showPromotions} />
    )) : <>
      <ProductTile key={displayItemKey(items[0])} item={items[0]} hero now={now} showPromotions={showPromotions} />
      {items.length > 1 && <div className="branch-display-supporting" style={{ gridTemplateColumns: `repeat(${items.length - 1}, minmax(0, 1fr))` }}>
        {items.slice(1).map((item, index) => <ProductTile key={displayItemKey(item)} item={item} index={index} now={now} showPromotions={showPromotions} />)}
      </div>}
    </>}
  </div>
);

const BranchDisplayPlayer = ({ display, preview = false, onChooseBranch, connectionStatus }) => {
  const root = useRef(null);
  const wakeLock = useDisplayWakeLock(!preview);
  // Some TV browsers report a native lock without controlling the TV's own idle policy.
  const videoFallback = useDisplayVideoFallback(!preview);
  const hideTimer = useRef(null);
  const items = (display?.items || []).filter((item) => item.available !== false
    && item.regularPrice !== null && item.regularPrice !== undefined && item.regularPrice !== ''
    && Number.isFinite(Number(item.regularPrice)) && Number(item.regularPrice) >= 0);
  const enabled = preview || Boolean(display?.enabled);
  const duration = Math.min(60, Math.max(5, Number(display?.slideDurationSeconds) || 10));
  const [activeId, setActiveId] = useState(() => displayItemKey(items[0]));
  const [paused, setPaused] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [fullscreenMessage, setFullscreenMessage] = useState('');
  const [wakeLockHelpVisible, setWakeLockHelpVisible] = useState(false);
  const wakeLockHelp = videoFallback.status === 'playing'
    ? 'Modo TV: reproducción continua sin sonido. Si el TV aún entra en reposo, revisa su temporizador y apagado automático.'
    : videoFallback.status === 'blocked'
      ? 'Pulsa Evitar reposo con el control remoto para iniciar el modo TV.'
      : 'Si no se activa el modo TV, revisa el temporizador de reposo y el apagado automático del equipo.';
  const now = useBranchDisplayClock(display);
  const slides = displaySlides(items, display?.productsPerSlide);
  const showPromotions = display?.showPromotions !== false;
  const slideIndex = displaySlideIndex(slides, activeId);
  const slide = slides[slideIndex] || [];
  const heroId = displayItemKey(slide[0]);
  const latestSlides = useRef(slides);
  latestSlides.current = slides;
  const lastSlide = useRef(null);
  const transitionTimer = useRef(null);
  const [previousSlide, setPreviousSlide] = useState(null);

  useEffect(() => { if (heroId !== activeId) setActiveId(heroId); }, [heroId, activeId]);
  useLayoutEffect(() => {
    if (lastSlide.current && lastSlide.current.key !== heroId) {
      window.clearTimeout(transitionTimer.current);
      setPreviousSlide(lastSlide.current.items);
      transitionTimer.current = window.setTimeout(() => setPreviousSlide(null), 450);
    }
    // Keep the outgoing snapshot current, including price/image-only refreshes.
    lastSlide.current = { key: heroId, items: slide };
  });
  useEffect(() => () => window.clearTimeout(transitionTimer.current), []);

  useEffect(() => { if (!preview) root.current?.focus({ preventScroll: true }); }, [preview]);

  const advance = useCallback((direction) => {
    const currentSlides = latestSlides.current;
    if (!currentSlides.length) return;
    setActiveId((current) => {
      const index = displaySlideIndex(currentSlides, current);
      return displayItemKey(currentSlides[(index + direction + currentSlides.length) % currentSlides.length][0]);
    });
  }, []);

  useEffect(() => {
    if (!enabled || paused || slides.length < 2) return undefined;
    const timer = window.setInterval(() => advance(1), duration * 1000);
    return () => window.clearInterval(timer);
  }, [advance, duration, enabled, slides.length, paused]);

  useEffect(() => {
    const currentSlides = latestSlides.current;
    if (!enabled || !currentSlides.length) return;
    const index = displaySlideIndex(currentSlides, heroId);
    const upcoming = [0, 1, 2].flatMap((offset) => currentSlides[(index + offset) % currentSlides.length]);
    [...new Set(upcoming.map((item) => item.imageUrl).filter(Boolean))].forEach(prepareDisplayImage);
  // Only preload when a slide or catalog changes, not when controls are used.
  }, [enabled, heroId, display?.items, display?.productsPerSlide]);

  const revealControls = useCallback(() => {
    setControlsVisible(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setControlsVisible(false), 3000);
  }, []);
  useEffect(() => {
    revealControls();
    return () => window.clearTimeout(hideTimer.current);
  }, [revealControls]);

  const toggleFullscreen = useCallback(async () => {
    setWakeLockHelpVisible(false);
    setFullscreenMessage('');
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (root.current?.requestFullscreen) await root.current.requestFullscreen();
      else setFullscreenMessage('Usa la opción de pantalla completa de tu navegador.');
    } catch {
      setFullscreenMessage('No se pudo abrir la pantalla completa. Usa la opción de tu navegador.');
    }
  }, []);
  useEffect(() => {
    const changed = () => setFullscreen(document.fullscreenElement === root.current);
    document.addEventListener('fullscreenchange', changed);
    return () => document.removeEventListener('fullscreenchange', changed);
  }, []);

  const keyControls = (event) => {
    if (/^(INPUT|SELECT|TEXTAREA)$/.test(event.target.tagName)
      || (event.target.tagName === 'BUTTON' && [' ', 'Enter'].includes(event.key))) return;
    if (['ArrowRight', 'ArrowLeft', ' ', 'k', 'f'].includes(event.key)) {
      event.preventDefault();
      revealControls();
      if (event.key === 'ArrowRight') advance(1);
      if (event.key === 'ArrowLeft') advance(-1);
      if (event.key === ' ' || event.key === 'k') setPaused((value) => !value);
      if (event.key === 'f') toggleFullscreen();
    }
  };

  return (
    <section
      ref={root}
      className={`branch-display-player ${preview ? 'branch-display-preview' : 'branch-display-public'} ${controlsVisible ? '' : 'branch-display-idle'}`}
      aria-label="Pantalla de destacados"
      tabIndex={0}
      onKeyDown={keyControls}
      onPointerMove={revealControls}
      onPointerDown={revealControls}
      onFocus={revealControls}
    >
      {!preview && <video ref={videoFallback.videoRef} className="branch-display-keep-awake-video" muted loop playsInline preload="none" aria-hidden="true" tabIndex={-1} disablePictureInPicture />}
      <div className="branch-display-stage">
        <header className="branch-display-header">
          <span className="branch-display-brand">{display?.companyName || 'Bienvenidos'}</span>
          <span className="branch-display-branch">{[display?.branchName, display?.name].filter(Boolean).join(' · ')}</span>
        </header>
        <div className="branch-display-content">
          {enabled && slide.length > 0 ? <>
            {previousSlide?.length > 0 && <SlideBoard items={previousSlide} now={now} showPromotions={showPromotions} previous />}
            <SlideBoard key={heroId} items={slide} now={now} showPromotions={showPromotions} />
          </> : <div className="branch-display-empty">
            <p>Qué bueno tenerte aquí.</p>
            <span>Pronto, más destacados para ti.</span>
          </div>}
        </div>
      </div>
      <div
        className={`branch-display-controls ${controlsVisible ? 'branch-display-controls-visible' : ''}`}
        role="group"
        aria-label="Controles de presentación"
        onClick={(event) => { if (event.detail > 0) root.current?.focus({ preventScroll: true }); }}
      >
        {onChooseBranch && <button type="button" onClick={onChooseBranch} className="branch-display-choose-branch" aria-label="Elegir sucursal" title="Elegir sucursal"><FiChevronLeft /><span>Sucursales</span></button>}
        <button type="button" onClick={() => advance(-1)} disabled={!enabled || slides.length < 2} aria-label="Destacado anterior" title="Anterior (←)"><FiChevronLeft /></button>
        <button type="button" onClick={() => setPaused((value) => !value)} disabled={!enabled || slides.length < 2} aria-label={paused ? 'Reanudar presentación' : 'Pausar presentación'} title={paused ? 'Reanudar (espacio)' : 'Pausar (espacio)'}>{paused ? <FiPlay /> : <FiPause />}</button>
        <button type="button" onClick={() => advance(1)} disabled={!enabled || slides.length < 2} aria-label="Siguiente destacado" title="Siguiente (→)"><FiChevronRight /></button>
        <span className="branch-display-count">{enabled && slides.length ? `${slideIndex + 1} / ${slides.length}` : '—'}</span>
        {!preview && <button type="button" className="branch-display-wake-lock" onClick={() => {
          setFullscreenMessage('');
          setWakeLockHelpVisible(true);
          revealControls();
          wakeLock.request();
          videoFallback.request();
        }} title={wakeLock.status === 'active' ? 'Se solicitó mantener la pantalla encendida.' : wakeLockHelp} aria-label={wakeLock.status === 'active' ? 'Pantalla sin reposo' : 'Mantener pantalla encendida'}>{wakeLock.status === 'active' ? 'Sin reposo ✓' : videoFallback.status === 'playing' ? 'Modo TV activo' : 'Evitar reposo'}</button>}
        <button type="button" onClick={toggleFullscreen} aria-label={fullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'} title="Pantalla completa (f)">{fullscreen ? <FiMinimize /> : <FiMaximize />}</button>
      </div>
      {connectionStatus === 'offline' && <p role="status" className="branch-display-connection-status">{display ? 'Sin conexión · Última actualización disponible' : 'No se pudo conectar · Reintentando automáticamente'}</p>}
      {fullscreenMessage && <p className="branch-display-fullscreen-message" role="status">{fullscreenMessage}</p>}
      {!preview && wakeLockHelpVisible && controlsVisible && wakeLock.status !== 'active' && <p className="branch-display-fullscreen-message" role="status">{wakeLockHelp}</p>}
    </section>
  );
};

export default BranchDisplayPlayer;
